import type { ReactNode } from 'react';
import { Box, Drawer, IconButton } from '@mui/material';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { NavItem } from './nav';
import type { WhoamiCustomer } from '../api/whoami';
import { SidebarAccountMenu } from './SidebarAccountMenu';
import logoOnDark from '../assets/decisionpoint-logo-dark-bg.png';

export const SIDEBAR_WIDTH = 248;

/** Solid accent count badge (e.g. pending requests) shown on a nav row. */
function CountBadge({ count }: { count: number }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 18,
        height: 18,
        px: '5px',
        borderRadius: 999,
        bgcolor: 'brand.accent',
        color: '#fff',
        fontFamily: (theme) => theme.typography.button.fontFamily,
        fontWeight: 700,
        fontSize: 11,
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {count > 99 ? '99+' : count}
    </Box>
  );
}

/**
 * A single navy nav row (prototype `SidebarRow`): full-width bar, 44px tall, a
 * 3px orange left rail + solid active-blue fill when active, an 18px leading
 * icon, and an optional count badge. Uses `NavLink` so the active state tracks
 * the route (and stays active on descendant routes like `/users/:id`).
 */
function NavRow({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  return (
    <Box
      component={NavLink}
      to={item.to}
      onClick={onNavigate}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        minHeight: 44,
        py: '10px',
        pl: '13px',
        pr: 2,
        mb: '2px',
        textDecoration: 'none',
        borderLeft: '3px solid transparent',
        color: 'brand.sidebarFg',
        fontFamily: (theme) => theme.typography.button.fontFamily,
        fontSize: 14,
        lineHeight: 1,
        fontWeight: 500,
        transition: 'background 120ms ease',
        '& .nav-icon': { color: 'brand.sidebarFgMuted' },
        '&:hover': { bgcolor: 'brand.sidebarHoverBg' },
        '&.active': {
          bgcolor: 'brand.sidebarActiveBg',
          color: 'brand.sidebarActiveFg',
          borderLeftColor: 'brand.accent',
          fontWeight: 600,
          '& .nav-icon': { color: 'brand.sidebarActiveFg' },
        },
      }}
    >
      <Icon className="nav-icon" sx={{ fontSize: 18 }} />
      <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
        {t(item.labelKey)}
      </Box>
      {item.badge != null && <CountBadge count={item.badge} />}
    </Box>
  );
}

function SidebarBrand({
  customer,
  isMobile,
  onMobileClose,
}: {
  customer: WhoamiCustomer | null;
  isMobile: boolean;
  onMobileClose: () => void;
}) {
  const { t } = useTranslation();
  const logoUrl = customer?.logoUrl ?? logoOnDark;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: '18px',
        minHeight: 64,
        flexShrink: 0,
      }}
    >
      {/*
        `height` (not `maxHeight`) so every customer's logo renders at the same
        optical height. BrandImageProcessor scales uploads DOWN to fit 600x200
        but never up, so a max-height would render each customer's mark at
        whatever size they happened to upload — and SVGs, which ARE upscaled to
        fill the box, would keep full size while raster uploads shrank.

        `maxWidth` is the constraint that actually binds for wide wordmarks:
        once a logo's aspect ratio exceeds maxWidth/height, the box clamps to
        maxWidth and `objectFit: contain` fits to the WIDTH, so the painted
        height falls below `height` and raising `height` further does nothing.
        186 = 27 x 6.9, which clears the widest mark in use (412x60 = 6.87:1).
        `objectFit` stays because without it the clamped box would stretch the
        image horizontally rather than letterbox it.

        The row has 212px of usable width (SIDEBAR_WIDTH 248 - 18px padding
        each side). On mobile the close button (~36px) and the 8px gap share
        that, leaving 168 — declared explicitly so the image is never left to
        flex-shrink to an arbitrary size.
      */}
      <Box
        component="img"
        src={logoUrl}
        alt={customer?.name ?? t('app.name')}
        sx={{
          height: 27,
          width: 'auto',
          maxWidth: { xs: 168, md: 186 },
          objectFit: 'contain',
        }}
      />
      {isMobile && (
        <IconButton
          onClick={onMobileClose}
          aria-label={t('actions.close')}
          sx={{ color: 'brand.sidebarFg' }}
        >
          <CloseOutlined fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
}

interface SidebarContentProps {
  items: NavItem[];
  customer: WhoamiCustomer | null;
  workspaceLabelKey: string;
  /** Optional slot under the logo (the dual-role workspace switcher). */
  topSlot?: ReactNode;
  isMobile: boolean;
  onMobileClose: () => void;
}

function SidebarContent({
  items,
  customer,
  workspaceLabelKey,
  topSlot,
  isMobile,
  onMobileClose,
}: SidebarContentProps) {
  const { t } = useTranslation();
  const onNavigate = isMobile ? onMobileClose : undefined;
  // Settings is pinned below a divider (prototype chrome); its sub-nav renders
  // in the content area, not the rail, so it reads as a flat row here.
  const settingsItem = items.find((item) => item.children?.length);
  const primaryItems = items.filter((item) => !item.children?.length);

  return (
    <Box
      sx={{
        width: SIDEBAR_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'brand.sidebarBg',
        color: 'brand.sidebarFg',
        borderRight: '1px solid',
        borderColor: 'brand.sidebarBorder',
      }}
    >
      <SidebarBrand customer={customer} isMobile={isMobile} onMobileClose={onMobileClose} />
      {topSlot}
      <Box
        component="nav"
        aria-label={t('shell.primaryNavigation')}
        sx={{ flex: 1, overflowY: 'auto', py: '6px' }}
      >
        {primaryItems.map((item) => (
          <NavRow key={item.key} item={item} onNavigate={onNavigate} />
        ))}
        {settingsItem && (
          <>
            <Box sx={{ height: '1px', bgcolor: 'brand.sidebarDivider', m: '10px 16px' }} />
            <NavRow item={settingsItem} onNavigate={onNavigate} />
          </>
        )}
      </Box>
      <SidebarAccountMenu workspaceLabelKey={workspaceLabelKey} />
    </Box>
  );
}

interface SidebarProps extends Omit<SidebarContentProps, 'onMobileClose'> {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

/**
 * Navy, role-aware + module-aware sidebar. Permanent rail on desktop, temporary
 * drawer on mobile. Nav items, the workspace label, and the optional dual-role
 * switcher (`topSlot`) are derived from `whoami` by the caller (`AppShell`).
 */
export function Sidebar({ isMobile, mobileOpen, onMobileClose, ...contentProps }: SidebarProps) {
  const content = (
    <SidebarContent {...contentProps} isMobile={isMobile} onMobileClose={onMobileClose} />
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        slotProps={{ backdrop: { sx: { bgcolor: 'rgba(4, 29, 51, 0.45)' } } }}
        sx={{ '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH, border: 0 } }}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Box component="aside" sx={{ width: SIDEBAR_WIDTH, flexShrink: 0 }}>
      {content}
    </Box>
  );
}
